import React from 'react';

export interface StatCard {
  icon: React.ComponentType<{ className?: string }>;
  value: string | number;
  label: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
  secondaryValue?: string;
  secondaryLabel?: string;
}

interface StatCardsCarouselProps {
  title: string;
  stats: StatCard[];
}

const StatCardsCarousel: React.FC<StatCardsCarouselProps> = ({ title, stats }) => {
  const colorClasses = {
    blue: {
      border: 'border-primary-500',
      iconBg: 'bg-primary-50',
      iconColor: 'text-primary-600',
      textColor: 'text-primary-600',
    },
    green: {
      border: 'border-green-500',
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      textColor: 'text-green-600',
    },
    purple: {
      border: 'border-purple-500',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      textColor: 'text-purple-600',
    },
    orange: {
      border: 'border-orange-500',
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      textColor: 'text-orange-600',
    },
  };

  return (
    <div className="mt-8 mb-2">
      <h2 className="text-fluid-h2 font-semibold text-heading mb-4">{title}</h2>
      
      {/* Mobile Carousel */}
      <div 
        className="md:hidden overflow-x-auto -mx-4 px-4"
        style={{ 
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <style>{`
          .stat-carousel::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <div className="stat-carousel flex gap-4 snap-x snap-mandatory scroll-smooth pb-2">
          {stats.map((stat, index) => {
            const colors = colorClasses[stat.color];
            const Icon = stat.icon;
            
            return (
              <div 
                key={index}
                className={`flex-shrink-0 w-[42vw] max-w-[180px] snap-start bg-surface rounded-lg shadow-md p-3 border-l-4 ${colors.border}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`p-1.5 ${colors.iconBg} rounded-lg mb-2`}>
                    <Icon className={`h-5 w-5 ${colors.iconColor}`} />
                  </div>
                  {stat.secondaryValue ? (
                    <>
                      <div className={`text-sm font-bold ${colors.textColor} truncate w-full px-1`}>
                        {stat.value}
                      </div>
                      <div className={`text-xl font-bold ${colors.textColor}`}>
                        {stat.secondaryValue}
                      </div>
                      <div className="text-xs text-body">
                        {stat.secondaryLabel || stat.label}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`text-fluid-stat font-bold font-mono ${colors.textColor}`}>
                        {stat.value}
                      </div>
                      <div className="text-xs text-body">{stat.label}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop Grid */}
      <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const colors = colorClasses[stat.color];
          const Icon = stat.icon;
          
          return (
            <div 
              key={index}
              className={`bg-surface rounded-lg shadow-md p-4 border-l-4 ${colors.border}`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className={`p-2 ${colors.iconBg} rounded-lg`}>
                  <Icon className={`h-6 w-6 ${colors.iconColor}`} />
                </div>
                <div className="flex-1">
                  {stat.secondaryValue ? (
                    <>
                      <div className={`text-lg font-bold ${colors.textColor} truncate`}>
                        {stat.value}
                      </div>
                      <div className={`text-2xl font-bold ${colors.textColor}`}>
                        {stat.secondaryValue}
                      </div>
                      <div className="text-sm text-body">
                        {stat.secondaryLabel || stat.label}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`text-fluid-stat font-bold font-mono ${colors.textColor}`}>
                        {stat.value}
                      </div>
                      <div className="text-sm text-body">{stat.label}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatCardsCarousel;

